import _                                                                                      from 'underscore';
import React, { FC, useContext, useEffect, useRef }                                           from 'react';
import { Moment }                                                                             from 'moment';
import { Header, Icon, Label, List, ListProps, Popup, Segment, SegmentProps, SemanticCOLORS } from 'semantic-ui-react';
import { Cell, Grid }                                                                         from 'styled-css-grid';

import { ProjectService, ResourcesService, TimeService }                                                                                                                          from '../../../../services';
import { CorvalProjectActivity, CorvalSubObjective, Size, SystemEvent, Uuid }                                                                                                     from '../../../../types';
import { DecisionEngineQueries }                                                                                                                                                  from '../../../../queries';
import { ProjectActivityEntityChangeCallback, ProjectActivityStatusChangeCallback, useFeatureFlags, useProjectActivities, useProjectTermDictionary, useSidebar, useSystemModals } from '../../../../hooks';
import { CONSTANTS }                                                                                                                                                              from '../../../../constants';
import { getFieldWithProjectIdAccessor, runIfFunction, sumByAttribute }                                                                                                           from '../../../../util';
import { ACTIVITY_CRITICALITY, ACTIVITY_CRITICALITY_INFO, ENTITY_ICONS }                                                                                                          from '../../../../constants/ProductEnums';
import { ENVIRONMENT_IS_DEVELOPMENT, ENVIRONMENT_IS_TESTING }                                                                                                                     from '../../../../constants/SystemEnums';
import { CorvalDeepContentSidebar, SidebarContainer, SidebarDeepContentBackBar, StyledContainer }                                                                                 from '../../../containers';
import { getOffScreenNodeElement }                                                                                                                                                from '../../../containers/OffScreenNode';
import { EntityNote }                                                                                                                                                             from '../../../components/annotations/EntityNote';
import { ItemFromQuery }                                                                                                                                                          from '../../../components/from-query/ItemFromQuery';
import { StringOrEmpty }                                                                                                                                                          from '../../../components/display/StringOrEmpty';
import { Tooltip }                                                                                                                                                                from '../../../components/util/messaging/ToolTip';
import { HumanizedDateRangeLabel }                                                                                                                                                from '../../../components/labels/HumanizedDateRangeLabel';
import { FinePrintMessage }                                                                                                                                                       from '../../../components/util/messaging/FinePrintMessage';
import { BusinessFunctionDot }                                                                                                                                                    from '../../../components/product/BusinessFunctionDot';
import { TextLink }                                                                                                                                                               from '../../../components/display/TextLink';
import { InlineLoadingIndicatorLabel }                                                                                                                                            from '../../../components/util/loading/InlineLoadingIndicatorLabel';
import { ListFromQuery }                                                                                                                                                          from '../../../components/from-query/ListFromQuery';
import { CurrencyValue }                                                                                                                                                          from '../../../components/display/CurrencyValue';
import { ActivityCriticalityLabel }                                                                                                                                               from '../../../components/activities/ActivityCriticalityLabel';
import { ProjectContext }                                                                                                                                                         from '../ProjectIndexPage';
import { ObjectiveDetails }                                                                                                                                                       from '../objectives/details/ObjectiveDetails';
import { ProjectActivityStatusUpdateControl }                                                                                                                                     from './ProjectActivityStatusUpdateControl';
import { ProjectActivityUpdateDialog }                                                                                                                                            from './ProjectActivityUpdateDialog';
import { ProjectActivityDetailsOperationCost }                                                                                                                                    from './ProjectActivityDetailsOperationCost';
import { ProjectActivityDetailsOngingCost }                                                                                                                                       from './ProjectActivityDetailsOngoingCost';
import { ProjectQueries }                                                                                                                                                         from '../../../../queries/ProjectQueries';
import { ActivityRecurrenceSubHeader }                                                                                                                                            from './ActivityRecurrenceSubHeader';
import { EntityWatchers }                                                                                                                                                         from '../../../components/watchers/EntityWatchers';
import { TagCloud }                                                                                                                                                               from '../../../components/tag/TagCloud';
import { EntityDetailMilestone }                                                                                                                                                  from '../common/entities/EntityDetailMilestone';
import { ProjectActivityDetailsRecurrenceMilestoneList }                                                                                                                          from './ProjectActivityDetailsRecurrenceMilestoneList';
import { QueryResult }                                                                                                                                                            from '@apollo/react-common';
import { ProjectActivityDetailItemContainer }                                                                                                                                     from './ProjectActivityDetailItemContainer';
import { ErrorBoundary }                                                                                                                                                          from '../../../components/util/errorHandling/ErrorBoundary';
import { ActivityTimingLabel }                                                                                                                                                    from '../../../components/activities/ActivityTimingLabel';
import { ActivityLateCompletionMessage }                                                                                                                                          from '../../../components/activities/ActivityLateCompletionMessage';
import { queriesHaveData }                                                                                                                                                        from "../../../../store/_apolloUtil";


export interface ProjectActivityDetailsProps {
    id: Uuid,
    projectId?: Uuid,
    organizationId?: Uuid,
    onStatusChange?: ProjectActivityStatusChangeCallback,
    onEntityChange?: ProjectActivityEntityChangeCallback,
    closeUpdateDialogOnEntityUpdate?: boolean,
    requireEntityChangeSet?: boolean,
    requiredChangeSetAddProperties?: string,
    requiredChangeSetUpdateProperties?: string,
    onTagClick?: ( index: number, tag: string ) => void,
    showHeader?: boolean,
    showAnnotations?: boolean,
    showDescription?: boolean,
    showProgress?: boolean,
    showDuration?: boolean,
    showMilestoneInfo?: boolean,
    showCriticality?: boolean,
    showBusinessFunction?: boolean,
    showParentDependencies?: boolean,
    showChildDependencies?: boolean,
    showSupportingObjective?: boolean,
    showQuickLinks?: boolean,
    showRecurrences?: boolean,
    showBudgetDetails?: boolean,
    showHiringDetails?: boolean,
    showTooltips?: boolean,
    showTags?: boolean,
    showWatchers?: boolean,
    showIds?: boolean,
    showEditDisplayOptions?: boolean,
    canEditDisplayOptions?: boolean,
    canEditRecurrences?: boolean,
    canEditTiming?: boolean,
    canEditWatchers?: boolean,
    canEditResourcing?: boolean,
    includeObjectiveLinks?: boolean,
    includeParentDependencyLinks?: boolean,
    includeChildDependencyLinks?: boolean,
    durationSummaryLabel?: boolean,
    listProps?: ListProps,
    segmentProps?: SegmentProps,
    nestedContent?: number,
}

interface ProjectActivityDetailItem extends CorvalProjectActivity {
    startDate: Moment,
    endDate: Moment,
    duration: number,
    activity: CorvalProjectActivity['activity'] & { businessFunctionFormatted: string }
}

const defaultProps: ProjectActivityDetailsProps = {
    id:                                null,
    projectId:                         null,
    organizationId:                    null,
    onStatusChange:                    null,
    onEntityChange:                    null,
    closeUpdateDialogOnEntityUpdate:   false,
    requireEntityChangeSet:            false,
    requiredChangeSetAddProperties:    null,
    requiredChangeSetUpdateProperties: null,
    showHeader:                        true,
    showAnnotations:                   true,
    showDescription:                   true,
    showProgress:                      true,
    showDuration:                      true,
    showMilestoneInfo:                 true,
    showCriticality:                   true,
    showBusinessFunction:              true,
    showParentDependencies:            true,
    showChildDependencies:             true,
    showSupportingObjective:           true,
    showQuickLinks:                    true,
    showRecurrences:                   true,
    showHiringDetails:                 true,
    showBudgetDetails:                 true,
    showTooltips:                      true,
    showWatchers:                      true,
    showTags:                          true,
    showIds:                           false,
    showEditDisplayOptions:            false,
    canEditDisplayOptions:             true,
    canEditRecurrences:                true,
    canEditTiming:                     false,
    canEditWatchers:                   true,
    canEditResourcing:                 false,
    includeObjectiveLinks:             true,
    includeParentDependencyLinks:      true,
    includeChildDependencyLinks:       true,
    durationSummaryLabel:              false,
    listProps:                         {},
    segmentProps:                      {},
    nestedContent:                     0,
}

const ProjectActivityDetails: FC<ProjectActivityDetailsProps> = ( props ) => {

    let projectContext              = useContext( ProjectContext ),
        modalActions                = useSystemModals(),
        { termDictionary, getTerm } = useProjectTermDictionary(),
        featureFlags                = useFeatureFlags(),
        projectId                   = projectContext.projectId ?? props.projectId ?? null,
        organizationId              = projectContext.organizationId ?? props.organizationId ?? null;

    let { isMilestoneRecurrence, hasRecurrence, hasLateCompletion }                   = useProjectActivities(),
        { milestoneRecurrenceStringDescription, periodicRecurrenceStringDescription } = useProjectActivities(),
        { getChangeSetProperties }                                                    = useProjectActivities(),
        secondaryContentSidebar                                                       = useSidebar(),
        [ , secondaryContentActions ]                                                 = secondaryContentSidebar;

    let projectActivityDetailsReqRef = useRef<QueryResult>();

    useEffect( () => {

        if( props.nestedContent > 0 ){
            console.log( { 'nested activity': props.nestedContent } );
        }

    }, [] )

    const handleObjectiveContentClicked = ( item: CorvalSubObjective ) => {

        secondaryContentActions.show(
            <SidebarContainer padded={ false } cellRows={ `auto 1fr` }>

                <SidebarDeepContentBackBar
                    label={ 'Back to Activity' }
                    onClick={ () => secondaryContentActions.toggle( false ) }
                />

                <ObjectiveDetails
                    id={ item.id }
                    projectId={ projectId }
                    organizationId={ organizationId }
                    projectDetails={ true }
                    showActivities={ false }
                    showProgress={ props.showProgress }
                    showTags={ props.showTags && featureFlags.enableProjectActivityMetatagViewsAndTools }
                    canEditObjectiveTiming={ featureFlags.enableRoadmapSubobjectiveTimingRescheduling }
                    onTagClick={ props.onTagClick }
                    includeActivityLinks={ true }
                    nestedContent={ props.nestedContent + 1 }
                />

            </SidebarContainer>, {
                width: 'very wide',

                // [ NOTE: Manual Close Behavior ]
                //
                // This sidebar may have objective details linkable inside it,
                // that can spawn dialogs, which when the user closes or touches any
                // area outside the sidebar will CLOSE this secondary sidebar.
                //
                // This is undesired behavior, so make the target to close
                // the sidebar an element we can't reach with the mouse.
                // User must manually close the sidebar, but it prevents
                // the user's workflow from being interrupted.
                ...( props.includeObjectiveLinks ? { target: getOffScreenNodeElement() } : {} )
            }
        )
    }

    const handleDependencyContentClicked = ( item: CorvalProjectActivity, dependencyType: 'parent' | 'child' = 'parent' ) => {

        let isParentDependencyType = ( dependencyType === 'parent' );

        secondaryContentActions.show(
            <SidebarContainer padded={ false } cellRows={ `auto 1fr` }>

                <SidebarDeepContentBackBar
                    label={ isParentDependencyType
                            ? 'Back to Child Activity'
                            : 'Back to Parent Activity'
                    }
                    onClick={ () => secondaryContentActions.toggle( false ) }
                />

                <ProjectActivityDetails
                    { ...props }
                    id={ item.id }
                    projectId={ projectId }
                    organizationId={ organizationId }
                    showSupportingObjective={ true }
                    showQuickLinks={ true }
                    includeParentDependencyLinks={ true }
                    includeChildDependencyLinks={ true }
                    showParentDependencies={ true }
                    showChildDependencies={ true }
                    includeObjectiveLinks={ true }
                    nestedContent={ props.nestedContent + 1 }
                />

            </SidebarContainer>, {
                width: 'very wide',

                // [ NOTE: Manual Close Behavior ]
                //
                // This sidebar may have objective details linkable inside it,
                // that can spawn dialogs, which when the user closes or touches any
                // area outside the sidebar will CLOSE this secondary sidebar.
                //
                // This is undesired behavior, so make the target to close
                // the sidebar an element we can't reach with the mouse.
                // User must manually close the sidebar, but it prevents
                // the user's workflow from being interrupted.
                ...( ( props.includeParentDependencyLinks || props.includeChildDependencyLinks ) ? { target: getOffScreenNodeElement() } : {} )
            }
        )
    }

    const projectActivityHasOverridesFor = ( overrideAtts: Array<keyof CorvalProjectActivity> = [] ) => {

        let hasCustomTiming = false;

        const { data } = projectActivityDetailsReqRef.current;

        if( queriesHaveData( projectActivityDetailsReqRef.current ) ){

            const [ projectActivity ] = data.projectActivites;

            hasCustomTiming = (
                _.compact( overrideAtts.map( att => ( projectActivity[ att ] ?? null ) ) ).length > 0
            );
        }

        return hasCustomTiming;
    }

    const handleEditProjectActivityRecurrence = ( e: SystemEvent ) => {

        modalActions.show(
            <ProjectActivityUpdateDialog
                key={ ( new Date() ).getMilliseconds() }
                projectId={ projectId }
                id={ props.id }
                editorAreas={ [ 'timing' ] }
                hideUpdateAction={ ( featureFlags ) => (

                    !featureFlags.enableProjectActivityRecurrenceEditing

                ) }
                editorAreaOptions={ {

                    showRecurrenceScheduling: true,

                } }
                requireChangeSet={ props.requireEntityChangeSet }
                requiredChangeSetAddProperties={ props.requiredChangeSetAddProperties ?? getChangeSetProperties( 'recurrences', projectId ) }
                requiredChangeSetUpdateProperties={ props.requiredChangeSetUpdateProperties ?? getChangeSetProperties( 'recurrences', projectId ) }
                onUpdateComplete={ ( updatedAttributes, projectActivityId, propagationCalled, propagationResultsData, entityChangeSet ) => {

                    // do something when edited
                    runIfFunction( props.onEntityChange, updatedAttributes, projectActivityId, propagationCalled, propagationResultsData, entityChangeSet );

                    // update this query
                    runIfFunction( projectActivityDetailsReqRef.current.refetch );

                } }
                onUpdateCanceled={ e => modalActions.toggle( false ) }
                closeOnEntityUpdate={ props.closeUpdateDialogOnEntityUpdate }
            />, {
                size:                 'small',
                closeOnDocumentClick: false,
                closeOnDimmerClick:   false,
                closeOnEscape:        false,
            } )
    }

    const handleEditProjectActivityTimingDisplay = ( e: SystemEvent ) => {

        modalActions.show(
            <ProjectActivityUpdateDialog
                key={ ( new Date() ).getMilliseconds() }
                projectId={ projectId }
                id={ props.id }
                editorAreas={ [ 'details' ] }
                editorAreaOptions={ {

                    showPerceivedAsContinuous: true,

                } }
                requireChangeSet={ props.requireEntityChangeSet }
                requiredChangeSetAddProperties={ props.requiredChangeSetAddProperties ?? getChangeSetProperties( 'activityTimingDisplay', projectId ) }
                requiredChangeSetUpdateProperties={ props.requiredChangeSetUpdateProperties ?? getChangeSetProperties( 'activityTimingDisplay', projectId ) }
                onUpdateComplete={ ( updatedAttributes, projectActivityId, propagationCalled, propagationResultsData, entityChangeSet ) => {

                    // do something when edited
                    runIfFunction( props.onEntityChange, updatedAttributes, projectActivityId, propagationCalled, propagationResultsData, entityChangeSet );

                } }
                onUpdateCanceled={ e => modalActions.toggle( false ) }
                closeOnEntityUpdate={ props.closeUpdateDialogOnEntityUpdate }
            />, {
                size:                 'small',
                closeOnDocumentClick: false,
                closeOnDimmerClick:   false,
                closeOnEscape:        false,
            } )
    }

    const handleEditProjectActivityResourcing = ( e: SystemEvent, projectActivity: CorvalProjectActivity ) => {

        /* Info message for volatility warning and timing caveats */
        let warningMessage = (
            <>Please note that this setting can change commercialization map budgeting.<br/><strong>Updates here may affect the hiring details and scheduling of this project.</strong></>
        );

        const showWarning = !projectActivityHasOverridesFor( [
            'hiresOverride',
        ] );

        modalActions.show(
            <ProjectActivityUpdateDialog
                key={ ( new Date() ).getMilliseconds() }
                projectId={ projectId }
                id={ props.id }
                allowPropagationAction={ true }
                warningContent={ showWarning ? warningMessage : null }
                editorAreas={ [ 'resources' ] }
                editorAreaOptions={ {

                    showHireTypes: true,
                    projectId,
                } }
                requireChangeSet={ props.requireEntityChangeSet }
                requiredChangeSetAddProperties={ props.requiredChangeSetAddProperties ?? getChangeSetProperties( 'activityResourcing', projectId ) }
                requiredChangeSetUpdateProperties={ props.requiredChangeSetUpdateProperties ?? getChangeSetProperties( 'activityResourcing', projectId ) }
                onUpdateComplete={ ( updatedAttributes, projectActivityId, propagationCalled, propagationResultsData, entityChangeSet ) => {

                    // do something when edited
                    runIfFunction( props.onEntityChange, updatedAttributes, projectActivityId, propagationCalled, propagationResultsData, entityChangeSet );

                    // update this query
                    runIfFunction( projectActivityDetailsReqRef.current.refetch );

                } }
                onUpdateCanceled={ e => modalActions.toggle( false ) }
                closeOnEntityUpdate={ props.closeUpdateDialogOnEntityUpdate }
            />, {
                size:                 'small',
                closeOnDocumentClick: false,
                closeOnDimmerClick:   false,
                closeOnEscape:        false,
            } )
    }

    const handleEditProjectActivityTiming = ( e: SystemEvent ) => {

        /* Info message for volatility warning and timing caveats */
        let warningMessage = (
            <>Please note that this setting can change commercialization map timing in unexpected ways.<br/><strong>Updates here will affect the objective this project activity is attached to.</strong></>
        )

        const showWarning = !projectActivityHasOverridesFor( [
            'startTimeOverrideUtc',
            'endTimeOverrideUtc',
            'projectTimingMilestoneOverride',
            'projectCustomTimingMilestoneId',
        ] );

        modalActions.show(
            <ErrorBoundary>
                <ProjectActivityUpdateDialog
                    key={ ( new Date() ).getMilliseconds() }
                    projectId={ projectId }
                    id={ props.id }
                    editorAreas={ [ 'timing' ] }
                    warningContent={ showWarning ? warningMessage : null }
                    hideUpdateAction={ ( featureFlags ) => (

                        !featureFlags.permissionProjectActivityUpdateStartDate &&
                        !featureFlags.permissionProjectActivityUpdateEndDate

                    ) }
                    editorAreaOptions={ {

                        showStartDate:            true,
                        showEndDate:              true,
                        showRecurrenceScheduling: false,

                    } }
                    requireChangeSet={ props.requireEntityChangeSet }
                    requiredChangeSetAddProperties={ props.requiredChangeSetAddProperties ?? getChangeSetProperties( 'activityTiming', projectId ) }
                    requiredChangeSetUpdateProperties={ props.requiredChangeSetUpdateProperties ?? getChangeSetProperties( 'activityTiming', projectId ) }
                    onUpdateComplete={ ( updatedAttributes, projectActivityId, propagationCalled, propagationResultsData, entityChangeSet ) => {

                        // do something when edited
                        runIfFunction( props.onEntityChange, updatedAttributes, projectActivityId, propagationCalled, propagationResultsData, entityChangeSet );

                        // update this query
                        runIfFunction( projectActivityDetailsReqRef.current.refetch );

                    } }
                    onUpdateCanceled={ e => modalActions.toggle( false ) }
                    closeOnEntityUpdate={ props.closeUpdateDialogOnEntityUpdate }
                />
            </ErrorBoundary>, {
                size:                 'small',
                closeOnDocumentClick: false,
                closeOnDimmerClick:   false,
                closeOnEscape:        false,
            } )
    }

    const handleFocusToDateRange = ( e: SystemEvent, date: Moment ) => {

    }

    const handleTagClicked = ( index, content ) => {

        runIfFunction( props.onTagClick, index, content );
    }

    return (

        <ItemFromQuery
            queryRef={ projectActivityDetailsReqRef }
            as={ React.Fragment }
            query={ DecisionEngineQueries.queries.GET_PROJECT_ACTIVITIES( `
				id
				startTimeUtc
				endTimeUtc
				displayName
				activityId
				completionState
				project{
					id ${ ProjectQueries.fragments.PROJECTS.TIMELINE_FIELDS_AND_DEFINED_TIMINGS() }
				}
				isRecurrence
			    recurrenceSourceProjectActivityId

			    ${ props.showRecurrences ? `
			    recurrenceUnit
			    recurrenceValue
			    recurrenceType
			    recurrenceMilestones{
			        recurrenceMilestone
			    }
				recurrenceCustomMilestones{
					projectCustomTimingMilestoneId
					projectCustomTimingMilestone {
						name
						date
					}
				}
			    ` : '' }
			    
			    ${ props.canEditTiming ? `
                startTimeOverrideUtc
                endTimeOverrideUtc
                defaultTimeSpanValueOverride
                defaultTimeSpanUnitOverride
                projectTimingMilestoneOverride
                projectCustomTimingMilestoneId
                projectTimingMilestoneOffsetMonthsOverride                        
                ` : '' }

			    ${ ( props.showBudgetDetails || props.showHiringDetails ) ? `
				requiredHires {
				   headcount
				   hireType { 
                        id 
                        ${ getFieldWithProjectIdAccessor( projectId, 'name' ) } 
                        ${ getFieldWithProjectIdAccessor( projectId, 'description' ) }
                        businessFunction 
                        salary 
                        modifier
                        managingHireType {
                            ${ getFieldWithProjectIdAccessor( projectId, 'name' ) }
                        }
				    }
				   isSatisfied( projectActivityId: "${ props.id }" )
				   satisfyingHires( projectActivityId: "${ props.id }" ) { id headcount hireDate hireTypeId note }
				}
				` : '' }

				${ props.showBudgetDetails ? `
				operationalCostOverride
				operationalCostOverrideNote
				ongoingCostOverride
				ongoingCostOverrideNote
				professionalFees {
					id
					feeDate
					amount
					professionalFee{
						id
						name
						description
						resourceCode
						amount
					}
				}` : '' }

				activity {
					id
					description
					category { id name }
					subCategory { id name }
					businessFunction
					projectTimingMilestone
					projectTimingCompletionMilestone

					${ props.showParentDependencies ? `parentActivities { parentActivity { id displayName } }` : '' }
					${ props.showChildDependencies ? `childActivities { childActivity { id displayName } }` : '' }
					${ props.showSupportingObjective ? `triggeringSubObjectives{ id displayName description }` : '' }
					${ props.showQuickLinks ? `quickLinks { text url ordinal}` : '' }

					defaultTimeSpanValue
					defaultTimeSpanUnit
					defaultCriticality
					
					${ props.showBudgetDetails ? `
					defaultBudgetValue
					defaultOperationalCost
					defaultOngoingCostMin
					defaultOngoingCostMax
					defaultOngoingCostDurationQuarters
					` : '' }

					${ props.showTags ? `
					tags{ tagName }
					` : '' }
				}
			` ) }
            queryOptions={ {
                variables:   {
                    projectId,
                    includeGeneratedRecurrences: true,
                    where:                       { id: { eq: props.id } }
                },
                fetchPolicy: 'network-only'
            } }
            queryResultObject={ data => {

                let item = _.first( data.projectActivites ) as ProjectActivityDetailItem;

                item.startDate = TimeService.ISOtoMoment( item.startTimeUtc );
                item.endDate = TimeService.ISOtoMoment( item.endTimeUtc );
                item.duration = TimeService.getDayDifferential( item.startDate, item.endDate );
                item.activity.businessFunctionFormatted = item.activity.businessFunction.name;

                return item;

            } }>

            { ( projectActivity: ProjectActivityDetailItem ) => {

                let { id, project, activityId, startDate, endDate, displayName, isRecurrence }       = projectActivity,
                    { description, businessFunctionFormatted }                                       = projectActivity.activity,
                    { projectTimingMilestone, projectTimingCompletionMilestone, defaultCriticality } = projectActivity.activity,
                    { parentActivities, childActivities, triggeringSubObjectives, quickLinks }       = projectActivity.activity,
                    { tags }                                                                         = projectActivity.activity,
                    hasParents                                                                       = !!( parentActivities?.length ?? 0 ),
                    hasChildren                                                                      = !!( childActivities?.length ?? 0 ),
                    hasObjectives                                                                    = !!( triggeringSubObjectives?.length ?? 0 ),
                    hasQuickLinks                                                                    = !!( quickLinks?.length ?? 0 );

                const projectTimelineFields = ProjectService.getTimelineDataObjectFromProjectFields( project, termDictionary );

                const hasLateCompletionTiming = hasLateCompletion(
                    projectActivity.endTimeUtc,
                    projectTimingCompletionMilestone,
                    projectTimelineFields
                )
                const completionTimelineField = ProjectService.getTimelineDataObjectFromSet( projectTimingCompletionMilestone, projectTimelineFields ),
                      completeByLabel         = completionTimelineField?.label ?? null,
                      completeByDate          = completionTimelineField?.value ?? TimeService.ISOtoDisplayDate( projectActivity.endTimeUtc );

                return (

                    <CorvalDeepContentSidebar
                        key={ props.id }
                        sidebar={ secondaryContentSidebar }
                        inset={ [ 10, 0, 0, ( 20 * ( props.nestedContent + 1 ) ) ] }>

                        <StyledContainer padding={ Size.MD }>

                            <Segment basic { ...props.segmentProps }>

                                {/* Distinction Headers */ }
                                { isRecurrence && <ActivityRecurrenceSubHeader/> }

                                {/* Header */ }
                                { props.showHeader && <Header dividing as={ 'h3' } content={ displayName } className={ isRecurrence ? 'no-margin-top' : null }/> }

                                {/* Description */ }
                                { props.showDescription && <StringOrEmpty evaluate={ description } passValue={ <FinePrintMessage>{ description }</FinePrintMessage> } emptyValue={ null }/> }

                                {/* Tags */ }
                                { props.showTags && <TagCloud tags={ tags } size={ 'tiny' } onClick={ _.isFunction( props.onTagClick ) ? handleTagClicked : null }/> }

                                {/* Progression tools */ }
                                { props.showProgress && (
                                    <Segment secondary size={ 'small' } style={ { padding: 5, marginBottom: 20 } }>

                                        <ProjectActivityStatusUpdateControl
                                            id={ props.id }
                                            projectId={ projectId }
                                            editable={ featureFlags.enableProjectActivityStatus }
                                            organizationId={ organizationId }
                                            withWarning={ true }
                                            withLastUpdated={ true }
                                            onStatusChange={ props.onStatusChange as ProjectActivityStatusChangeCallback }
                                        />

                                    </Segment>
                                ) }

                                <List relaxed verticalAlign={ 'top' } divided { ...props.listProps }>

                                    {/* Display Options */ }
                                    { featureFlags.enableProjectActivityDetailsDisplayOptions && props.showEditDisplayOptions && props.canEditDisplayOptions && (

                                        <List.Item className={ 'peek-container' }>

                                            <List.Content>
                                                <List.Description className={ 'text-right' }>

                                                    <TextLink className={ 'text--sm cursor--pointer opacity--100 opacity-hover--90' } onClick={ handleEditProjectActivityTimingDisplay }><strong>Display Options</strong></TextLink>

                                                </List.Description>
                                            </List.Content>

                                        </List.Item>
                                    ) }

                                    { props.showWatchers && (

                                        <ProjectActivityDetailItemContainer
                                            showTooltips={ props.showTooltips }
                                            icon={ ENTITY_ICONS.USER.WATCHER }
                                            itemLabel={ 'Following' }
                                            tooltip={ 'Users on this project who are watching this item.' }
                                            gridProps={ { columns: `auto minmax(90px, 1fr) auto` } }
                                            iconCellProps={ { middle: true } }
                                            labelCellProps={ { middle: true } }
                                            mainContentCellProps={ { middle: true, className: 'text-right' } }>

                                            <EntityWatchers
                                                entityType={ 'projectActivity' }
                                                entityId={ props.id }
                                                editable={ props.canEditWatchers }
                                                projectId={ projectId }
                                                organizationId={ organizationId }
                                                itemFlowDirection={ 'end' }
                                                menuDirection={ 'right' }
                                            />

                                        </ProjectActivityDetailItemContainer>
                                    ) }

                                    { props.showDuration && (

                                        <ProjectActivityDetailItemContainer
                                            showTooltips={ props.showTooltips }
                                            icon={ 'clock outline' }
                                            itemLabel={ 'Timeframe' }
                                            tooltip={ 'The timeframe this activity is expected to initially be scheduled for.' }
                                            gridProps={ { columns: `auto minmax(90px, 1fr) auto` } }
                                            iconCellProps={ { middle: true } }
                                            labelCellProps={ { middle: true } }
                                            mainContentCellProps={ { middle: true, className: 'text-right' } }
                                            additionalContent={ _.compact( [

                                                /* Late Completion Info */
                                                ( hasLateCompletionTiming && !isRecurrence ) && (
                                                    <Popup
                                                        size={ 'small' }
                                                        position={ 'top right' }
                                                        content={ <ActivityLateCompletionMessage completeByDate={ completeByDate } completeByLabel={ completeByLabel }/> }
                                                        trigger={ <div className={ 'text-right cursor--default' }><ActivityTimingLabel value={ 'late-completion' }/></div> }>
                                                    </Popup>
                                                )

                                            ] ) }>

                                            <HumanizedDateRangeLabel
                                                startDate={ startDate }
                                                endDate={ endDate }
                                                withSummaryLabel={ props.durationSummaryLabel }
                                                fromLabelProps={ props.canEditTiming && featureFlags.permissionProjectActivityUpdateStartDate ? {
                                                    color:     'red',
                                                    basic:     true,
                                                    className: 'cursor--pointer opacity--100 opacity-hover--90',
                                                    onClick:   handleEditProjectActivityTiming
                                                } : null }
                                                toLabelProps={ props.canEditTiming && featureFlags.permissionProjectActivityUpdateEndDate ? {
                                                    color:     'red',
                                                    basic:     true,
                                                    className: 'cursor--pointer opacity--100 opacity-hover--90',
                                                    onClick:   handleEditProjectActivityTiming
                                                } : null }
                                            />

                                        </ProjectActivityDetailItemContainer>
                                    ) }

                                    { props.showMilestoneInfo && projectTimingMilestone && (

                                        <ProjectActivityDetailItemContainer
                                            showTooltips={ props.showTooltips }
                                            icon={ ENTITY_ICONS.MILESTONES.ENTITY }
                                            itemLabel={ 'Milestone' }
                                            tooltip={ `Key milestone associated with this activity's starting timeframe.` }
                                            gridProps={ { columns: `auto 1fr minmax(auto, 60%)` } }>


                                            <EntityDetailMilestone
                                                fieldId={ projectTimingMilestone }
                                                projectId={ projectId }
                                                showDate={ true }
                                                offset={ 0 }
                                                popupProps={ { position: 'top right' } }
                                            />

                                        </ProjectActivityDetailItemContainer>
                                    ) }

                                    { props.showMilestoneInfo && projectTimingCompletionMilestone && (

                                        <ProjectActivityDetailItemContainer
                                            showTooltips={ props.showTooltips }
                                            icon={ ENTITY_ICONS.MILESTONES.ENTITY }
                                            itemLabel={ 'Complete By' }
                                            tooltip={ `Key milestone associated with this activity's recommended completion timeframe.` }
                                            gridProps={ { columns: `auto 1fr minmax(auto, 60%)` } }>

                                            <EntityDetailMilestone
                                                fieldId={ projectTimingCompletionMilestone }
                                                projectId={ projectId }
                                                showDate={ true }
                                                offset={ 0 }
                                                popupProps={ { position: 'top right' } }
                                            />

                                        </ProjectActivityDetailItemContainer>
                                    ) }

                                    { props.showRecurrences && (

                                        <ProjectActivityDetailItemContainer
                                            showTooltips={ props.showTooltips }
                                            icon={ ENTITY_ICONS.ACTIVITIES.RECURRENCE }
                                            itemLabel={ 'Recurrence' }
                                            tooltip={ 'The frequency of recurring times this activity is displayed on the map.' }>

                                            <>
                                                { !isRecurrence ? (

                                                    <>
                                                        {/* Clickable recurrence description label */ }
                                                        <Label circular basic color={ ( hasRecurrence( projectActivity ) ? props.canEditRecurrences ? 'red' : null : 'grey' ) as SemanticCOLORS }
                                                               icon={ { name: 'calendar alternate outline' } }
                                                               className={ props.canEditRecurrences ? 'cursor--pointer opacity--100 opacity-hover--90' : null }
                                                               onClick={ props.canEditRecurrences ? handleEditProjectActivityRecurrence : null }
                                                               content={ isMilestoneRecurrence( projectActivity )
                                                                         ? milestoneRecurrenceStringDescription( projectActivity )
                                                                         : periodicRecurrenceStringDescription( projectActivity )
                                                               }/>

                                                        {/* Milestones this PA recurrs on */ }
                                                        { isMilestoneRecurrence( projectActivity ) && (

                                                            <ProjectActivityDetailsRecurrenceMilestoneList
                                                                projectActivity={ projectActivity }
                                                                projectTimelineFields={ projectTimelineFields }
                                                            />
                                                        ) }
                                                    </>

                                                ) : (

                                                      <ItemFromQuery query={ DecisionEngineQueries.queries.GET_PROJECT_ACTIVITIES( `
																					  id
																					  startTimeUtc
																					  recurrenceUnit
																					  recurrenceValue
																					  recurrenceType
																					  recurrenceMilestones{ recurrenceMilestone }
																					  recurrenceCustomMilestones{ projectCustomTimingMilestoneId }
																				  ` ) }
                                                                     queryOptions={ { variables: { projectId, includeGeneratedRecurrences: true, where: { id: { eq: projectActivity.recurrenceSourceProjectActivityId } } } } }
                                                                     queryResultObject={ data => _.first( data.projectActivites ) }
                                                                     loadingComponent={ () => <InlineLoadingIndicatorLabel label={ 'Calculating...' }/> }>

                                                          {/* Parent's recurrence information */ }
                                                          { ( recurringParentProjectActivity: CorvalProjectActivity ) => {

                                                              return (

                                                                  <>
                                                                      <Label circular basic color={ ( hasRecurrence( recurringParentProjectActivity ) ? null : 'grey' ) as SemanticCOLORS }
                                                                             icon={ { name: 'calendar alternate outline' } }
                                                                             content={ isMilestoneRecurrence( recurringParentProjectActivity )
                                                                                       ? milestoneRecurrenceStringDescription( recurringParentProjectActivity )
                                                                                       : periodicRecurrenceStringDescription( recurringParentProjectActivity ) }/>

                                                                      { isMilestoneRecurrence( recurringParentProjectActivity ) && (

                                                                          <ProjectActivityDetailsRecurrenceMilestoneList
                                                                              projectActivity={ recurringParentProjectActivity }
                                                                              projectTimelineFields={ projectTimelineFields }
                                                                          />
                                                                      ) }
                                                                  </>
                                                              );
                                                          } }

                                                      </ItemFromQuery>
                                                  ) }
                                            </>

                                        </ProjectActivityDetailItemContainer>

                                    ) }

                                    { props.showCriticality && defaultCriticality && (

                                        <ProjectActivityDetailItemContainer
                                            showTooltips={ props.showTooltips }
                                            icon={ ENTITY_ICONS.ACTIVITIES.CRITICALITY_RATING }
                                            itemLabel={ 'Criticality' }
                                            tooltip={ 'The level at which Corval recommends an activity for successful commercialization.' }>


                                            <ActivityCriticalityLabel value={ ACTIVITY_CRITICALITY_INFO[ defaultCriticality ].VALUE as unknown as ACTIVITY_CRITICALITY } size={ 'small' }/>

                                        </ProjectActivityDetailItemContainer>
                                    ) }

                                    { props.showBusinessFunction && (

                                        <ProjectActivityDetailItemContainer
                                            showTooltips={ props.showTooltips }
                                            icon={ ENTITY_ICONS.BUSINESS_FUNCTIONS.ENTITY }
                                            itemLabel={ 'Business Function' }
                                            tooltip={ 'The team or department areas most likely to be tasked with an activity.' }>

                                            <><BusinessFunctionDot businessFunction={ projectActivity.activity.businessFunction }/> { businessFunctionFormatted }</>

                                        </ProjectActivityDetailItemContainer>
                                    ) }

                                    { props.showHiringDetails && (

                                        <ProjectActivityDetailItemContainer
                                            showTooltips={ props.showTooltips }
                                            icon={ ENTITY_ICONS.BUDGET.ENTITY_HIRING }
                                            itemLabel={ 'Resources' }
                                            tooltip={ 'A list of hiring resources this activity would need in order to avoid outsourcing or additional professional fees.' }
                                            mainContentCellProps={ { className: 'text-right', middle: true } }
                                            mainContent={ (
                                                <>
                                                    { ResourcesService.projectActivityRequiresResources( projectActivity ) ? (

                                                        <Label circular
                                                               size={ 'small' }
                                                               content={ ResourcesService.isProjectActivityFullyResourced( projectActivity ) ? 'All Hires Scheduled' : 'Hires Needed' }
                                                               icon={ ResourcesService.isProjectActivityFullyResourced( projectActivity ) ? { name: 'checkmark' } : null }
                                                               color={ ResourcesService.isProjectActivityFullyResourced( projectActivity ) ? 'green' : 'grey' }
                                                               basic={ !ResourcesService.isProjectActivityFullyResourced( projectActivity ) }
                                                               onClick={ ( props.canEditResourcing && featureFlags.permissionProjectActivityUpdateHireTypes ) ? ( e ) => handleEditProjectActivityResourcing( e, projectActivity ) : null }
                                                               className={ ( props.canEditResourcing && featureFlags.permissionProjectActivityUpdateHireTypes ) ? 'cursor--pointer opacity--100 opacity-hover--80' : null }
                                                        />

                                                    ) : (
                                                          <Label circular basic color={ 'grey' }
                                                                 icon={ { name: ENTITY_ICONS.RESOURCING.HIRES.ENTITY } }
                                                                 className={ ( props.canEditResourcing && featureFlags.permissionProjectActivityUpdateHireTypes ) ? 'cursor--pointer opacity--100 opacity-hover--90' : null }
                                                                 onClick={ ( props.canEditResourcing && featureFlags.permissionProjectActivityUpdateHireTypes ) ? ( e ) => handleEditProjectActivityResourcing( e, projectActivity ) : null }
                                                                 content={ 'None Required' }/>
                                                      ) }
                                                </>
                                            ) }
                                            additionalContent={ _.compact( [

                                                ResourcesService.projectActivityRequiresResources( projectActivity ) && (

                                                    <Grid columns={ `minmax(16px,1fr) auto auto` } columnGap={ '4px' } rowGap={ '2px' } className={ 'text--sm' }>

                                                        { projectActivity.requiredHires.map( ( requiredHire ) => {

                                                            let satisfyingHiresMatchingRequiredHireType = _.filter( requiredHire.satisfyingHires, h => ( h.hireTypeId === requiredHire.hireType.id ) ),
                                                                satisfyingHiresHeadCount                = sumByAttribute( satisfyingHiresMatchingRequiredHireType, 'headcount' ),
                                                                showHeadCountMultiplier                 = ( sumByAttribute( projectActivity.requiredHires, 'headcount' ) > 1 );

                                                            return (

                                                                <>
                                                                    {/* Icon indicator */ }
                                                                    <Cell className={ 'text-right' }>
                                                                        <Icon name={ 'user' } color={ requiredHire.isSatisfied ? 'teal' : 'grey' }/>
                                                                    </Cell>

                                                                    {/* Headcount needed */ }
                                                                    <Cell>
                                                                        { ( !requiredHire.isSatisfied )
                                                                          ? <>{ satisfyingHiresHeadCount } / { requiredHire.headcount }</>
                                                                          : showHeadCountMultiplier
                                                                            ? <>{ requiredHire.headcount } &times; </>
                                                                            : null }
                                                                    </Cell>

                                                                    {/* Hire type */ }
                                                                    <Popup key={ requiredHire.hireType.id }
                                                                           wide
                                                                           position={ 'top right' }
                                                                           className={ 'text-left' }
                                                                           content={

                                                                               <Grid columns={ `minmax(auto,12px) auto` }>

                                                                                   {/* Hire type business function */ }
                                                                                   <Cell><BusinessFunctionDot businessFunction={ requiredHire.hireType.businessFunction }/></Cell>

                                                                                   {/* Hire type name */ }
                                                                                   <Cell className={ 'text--sm text-height--sm' }><strong>{ requiredHire.hireType.name }</strong></Cell>

                                                                                   {/* Hire type description */ }
                                                                                   { featureFlags.displayHiringTypeDescriptions &&
                                                                                     <Cell width={ 2 }><FinePrintMessage>{ requiredHire.hireType.description }</FinePrintMessage></Cell> }

                                                                                   { /* Reports to */ }

                                                                                   <Cell className={ 'text--sm' } width={ 2 }>{ requiredHire.hireType.managingHireType ? <label><strong>Reports to:</strong> { requiredHire.hireType.managingHireType.name }</label> : null } </Cell>


                                                                                   {/* Hires made on specific dates, or not. */ }
                                                                                   <Cell width={ 2 }>

                                                                                       { ( satisfyingHiresHeadCount > 0 ) ? (

                                                                                           <List className={ 'no-margin-bottom no-padding-left' } size={ 'small' } divided>

                                                                                               {/* List of hires planned w/ hire dates */ }
                                                                                               { satisfyingHiresMatchingRequiredHireType.map( satisfyingHire => (

                                                                                                   <List.Item key={ satisfyingHire.id }>

                                                                                                       <Grid className={ 'text--sm' } columns={ `auto 1fr` } rowGap={ '0px' } columnGap={ showHeadCountMultiplier ? null : '0px' }>

                                                                                                           {/* Headcount for hire date */ }
                                                                                                           <Cell>{ showHeadCountMultiplier ? <>{ satisfyingHire.headcount } &times;</> : null }</Cell>

                                                                                                           {/* Hire date */ }
                                                                                                           <Cell><strong>Planned Hire Date:</strong> { TimeService.ISOtoDisplayDate( satisfyingHire.hireDate ) }</Cell>

                                                                                                           {/* Notes */ }
                                                                                                           { !_.isEmpty( satisfyingHire.note ) && (
                                                                                                               <Cell left={ 2 }>
                                                                                                                   <StyledContainer padding={ [ Size.XS, null ] }>
                                                                                                                       <FinePrintMessage as={ 'span' }>{ satisfyingHire.note }</FinePrintMessage>
                                                                                                                   </StyledContainer>
                                                                                                               </Cell>
                                                                                                           ) }

                                                                                                       </Grid>
                                                                                                   </List.Item>
                                                                                               ) ) }
                                                                                           </List>

                                                                                       ) : (

                                                                                             <FinePrintMessage>No planned hires available.</FinePrintMessage>

                                                                                         ) }
                                                                                   </Cell>
                                                                               </Grid>
                                                                           }
                                                                           trigger={ (

                                                                               ( props.canEditResourcing && featureFlags.permissionProjectActivityUpdateHireTypes )

                                                                               ? ( <Cell className={ 'cursor--pointer opacity--100 opacity-hover--80' }>
                                                                                       <TextLink onClick={ handleEditProjectActivityResourcing }>{ requiredHire.hireType.name }</TextLink>
                                                                                   </Cell>
                                                                               )
                                                                               : <Cell>{ requiredHire.hireType.name }</Cell>

                                                                           ) }/>

                                                                </>

                                                            )
                                                        } ) }
                                                    </Grid> )
                                            ] ) }/>

                                    ) }

                                    { props.showBudgetDetails && (
                                        <>
                                            { _.compact( [

                                                /* Professional Fees - Only for unresourced project activity items */
                                                ( ResourcesService.projectActivityRequiresResources( projectActivity ) &&
                                                  ResourcesService.isProjectActivityFullyResourced( projectActivity ) ) ? null : ( {
                                                    icon:    ENTITY_ICONS.BUDGET.FEE_COSTS,
                                                    label:   getTerm( 'BUDGET_COSTS.PROFESSIONAL_FEE_COSTS.LABEL' ),
                                                    tooltip: getTerm( 'BUDGET_COSTS.PROFESSIONAL_FEE_COSTS.DESCRIPTION' ),
                                                    content: <CurrencyValue value={ ResourcesService.getTotalInitialActivityFees( projectActivity )[ 1 ] } emptyOnZero={ true }/>,
                                                } ),

                                                /* Operational Cost */
                                                {
                                                    icon:    ENTITY_ICONS.BUDGET.OPERATIONAL_COSTS,
                                                    label:   getTerm( 'BUDGET_COSTS.OPERATIONAL_COSTS.LABEL' ),
                                                    tooltip: getTerm( 'BUDGET_COSTS.OPERATIONAL_COSTS.DESCRIPTION' ),
                                                    content: <ProjectActivityDetailsOperationCost projectActivity={ projectActivity }/>
                                                },

                                                /* Ongoing Costs - Recurrence Project Activity items do not carry ongoing costs forward */
                                                ( !projectActivity.isRecurrence ) &&
                                                {
                                                    icon:    ENTITY_ICONS.BUDGET.ONGOING_COSTS,
                                                    label:   `${ getTerm( 'BUDGET_COSTS.ONGOING_COSTS.LABEL' ) }`,
                                                    tooltip: getTerm( 'BUDGET_COSTS.ONGOING_COSTS.DESCRIPTION' ),
                                                    content: <ProjectActivityDetailsOngingCost projectActivity={ projectActivity }/>
                                                }
                                            ] ).map( ( detailItem, index ) => (

                                                <ProjectActivityDetailItemContainer
                                                    key={ index }
                                                    showTooltips={ props.showTooltips }
                                                    icon={ detailItem.icon }
                                                    itemLabel={ detailItem.label }
                                                    tooltip={ detailItem.tooltip }>

                                                    { detailItem.content }

                                                </ProjectActivityDetailItemContainer>
                                            ) ) }
                                        </>

                                    ) }

                                    { props.showAnnotations && (
                                        <List.Item>
                                            <List.Content>
                                                <EntityNote
                                                    id={ props.id }
                                                    projectId={ props.projectId }
                                                    type={ 'projectActivity' }
                                                    showLastEditedOnNote={ true }/>
                                            </List.Content>
                                        </List.Item>
                                    ) }
                                </List>

                            </Segment>

                            { props.showParentDependencies && hasParents && (
                                <Segment basic className={ 'peek-container' }>

                                    <Header as={ 'h4' } dividing>
                                        Prerequisites
                                        { props.showTooltips && <span className={ 'peek-item' }><Tooltip content={ 'Activities your team should focus on initiating (or completing) prior to this activity.' }/></span> }
                                    </Header>

                                    <ListFromQuery
                                        query={ DecisionEngineQueries.queries.GET_PROJECT_ACTIVITIES( `id displayName` ) }
                                        queryOptions={ {
                                            variables: {
                                                projectId,
                                                includeGeneratedRecurrences: false,
                                                where:                       {
                                                    activityId: {
                                                        in: parentActivities.map( pa => pa.parentActivity.id )
                                                    }
                                                }
                                            }
                                        } }
                                        queryResultObject={ data => data.projectActivites }
                                        loadingComponent={ () => <InlineLoadingIndicatorLabel label={ 'Loading activities...' }/> }
                                        containerComponent={ ( props: any ) => (
                                            <List relaxed verticalAlign={ 'top' }>{ props.children }</List>
                                        ) }
                                        itemComponent={ ( result: ( { item: CorvalProjectActivity } ) ) => (
                                            <List.Item key={ result.item.id }>
                                                <List.Icon name={ ENTITY_ICONS.ACTIVITIES.ENTITY } color={ 'teal' }/>
                                                <List.Content>
                                                    <List.Description>
                                                        {
                                                            ( props.includeParentDependencyLinks )
                                                            ? <TextLink onClick={ () => handleDependencyContentClicked( result.item, 'parent' ) } className={ 'text--colored-black' }>{ result.item.displayName }</TextLink>
                                                            : result.item.displayName
                                                        }
                                                    </List.Description>
                                                </List.Content>
                                            </List.Item>
                                        ) }
                                    />

                                </Segment>
                            ) }

                            { props.showQuickLinks && hasQuickLinks && (
                                <Segment basic className={ 'peek-container' }>

                                    <Header as={ 'h4' } dividing>
                                        Quick Links
                                        { props.showTooltips && <span className={ 'peek-item' }><Tooltip content={ 'Helpful resources that are related to this activity' }/></span> }
                                    </Header>

                                    <List relaxed verticalAlign={ 'top' }>

                                        { _.sortBy( quickLinks, 'ordinal' ).map( ( item, index ) => (

                                            index < 5 &&

                                            <List.Item key={ item.id }>
                                                <List.Icon name={ ENTITY_ICONS.META.LINKS } color={ 'teal' }/>
                                                <List.Content>
                                                    <List.Description>
                                                        {
                                                            ( props.includeObjectiveLinks )
                                                            ? <TextLink className={ 'text--colored-black' } target="_blank" href={ item.url }>{ item.text }</TextLink>
                                                            : item.text
                                                        }
                                                    </List.Description>
                                                </List.Content>
                                            </List.Item>

                                        ) ) }

                                    </List>

                                </Segment>
                            ) }

                            { props.showChildDependencies && hasChildren && (
                                <Segment basic className={ 'peek-container' }>

                                    <Header as={ 'h4' } dividing>
                                        Dependencies
                                        { props.showTooltips && <span className={ 'peek-item' }><Tooltip content={ 'Activities your team may want to focus on initiating once this activity has started.' }/></span> }
                                    </Header>

                                    <ListFromQuery
                                        query={ DecisionEngineQueries.queries.GET_PROJECT_ACTIVITIES( `id displayName` ) }
                                        queryOptions={ {
                                            variables: {
                                                projectId,
                                                includeGeneratedRecurrences: false,
                                                where:                       {
                                                    activityId: {
                                                        in: childActivities.map( ca => ca.childActivity.id )
                                                    }
                                                }
                                            }
                                        } }
                                        queryResultObject={ data => data.projectActivites }
                                        loadingComponent={ () => <InlineLoadingIndicatorLabel label={ 'Loading activities...' }/> }
                                        containerComponent={ ( props: any ) => (
                                            <List relaxed verticalAlign={ 'top' }>{ props.children }</List>
                                        ) }
                                        itemComponent={ ( result: ( { item: CorvalProjectActivity } ) ) => (
                                            <List.Item key={ result.item.id }>
                                                <List.Icon name={ ENTITY_ICONS.ACTIVITIES.ENTITY } color={ 'teal' }/>
                                                <List.Content>
                                                    <List.Description>
                                                        {
                                                            ( props.includeChildDependencyLinks )
                                                            ? <TextLink onClick={ () => handleDependencyContentClicked( result.item, 'child' ) } className={ 'text--colored-black' }>{ result.item.displayName }</TextLink>
                                                            : result.item.displayName
                                                        }
                                                    </List.Description>
                                                </List.Content>
                                            </List.Item>
                                        ) }
                                    />

                                </Segment>
                            ) }

                            { props.showSupportingObjective && hasObjectives && (
                                <Segment basic className={ 'peek-container' }>

                                    <Header as={ 'h4' } dividing>
                                        Supporting Objectives
                                        { props.showTooltips && <span className={ 'peek-item' }><Tooltip content={ 'The objectives that this activity is designed to help your team meet.' }/></span> }
                                    </Header>

                                    <List relaxed verticalAlign={ 'top' }>

                                        { triggeringSubObjectives.map( item => (

                                            <List.Item key={ item.id }>
                                                <List.Icon name={ ENTITY_ICONS.SUBOBJECTIVES.ENTITY } color={ 'teal' }/>
                                                <List.Content>
                                                    <List.Description>
                                                        {
                                                            ( props.includeObjectiveLinks )
                                                            ? <TextLink onClick={ () => handleObjectiveContentClicked( item ) } className={ 'text--colored-black' }>{ item.displayName }</TextLink>
                                                            : item.displayName
                                                        }
                                                    </List.Description>
                                                </List.Content>
                                            </List.Item>

                                        ) ) }

                                    </List>

                                </Segment>
                            ) }

                            { props.showIds && ENVIRONMENT_IS_DEVELOPMENT && !ENVIRONMENT_IS_TESTING && (
                                <Segment basic>

                                    <List relaxed verticalAlign={ 'top' } size={ 'mini' }>
                                        <List.Item className={ 'opacity--20 opacity-hover--50 cursor--pointer' }>
                                            <List.Header>Activity ID:</List.Header>
                                            <List.Content>{ activityId }</List.Content>
                                        </List.Item>
                                        <List.Item className={ 'opacity--20 opacity-hover--50 cursor--pointer' }>
                                            <List.Header>Project Activity ID:</List.Header>
                                            <List.Content>{ id }</List.Content>
                                        </List.Item>
                                    </List>

                                </Segment>
                            ) }

                        </StyledContainer>

                    </CorvalDeepContentSidebar>
                )
            } }

        </ItemFromQuery>
    );
};

ProjectActivityDetails.defaultProps = defaultProps;

export {
    ProjectActivityDetails
}
